import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Paper,
  IconButton,
} from '@mui/material';
import {
  Close as CloseIcon,
  Preview as PreviewIcon,
  Download as DownloadIcon,
  Print as PrintIcon,
  Email as EmailIcon,
  ContentCopy as CopyIcon,
} from '@mui/icons-material';
import { Order } from '../services/api';
import { SimpleInvoiceService, InvoiceData } from '../services/simpleInvoiceService';
import { SystemSettingsService } from '../services/systemSettingsService';
import { stripCheckoutMetadataFromNotes } from '../utils/orderNotes';
import { orderItemInvoiceDescription } from '../utils/orderItemLabel';

interface InvoiceGeneratorProps {
  open: boolean;
  onClose: () => void;
  order: Order | null;
}

const InvoiceGenerator: React.FC<InvoiceGeneratorProps> = ({
  open,
  onClose,
  order,
}) => {
  const [invoiceData, setInvoiceData] = useState<InvoiceData | null>(null);
  const [previewHtml, setPreviewHtml] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    if (open && order) {
      let cancelled = false;
      (async () => {
        setLoading(true);
        setPreviewHtml('');
        try {
          // Always refresh company from API settings (Kit Car etc.) before building invoice
          await SystemSettingsService.syncFromApi();

          // Prefer server invoice payload for line items; company from settings if server is stale
          const { apiUrl } = await import('../services/api');
          const token = localStorage.getItem('authToken');
          const res = await fetch(apiUrl(`/invoices/data/${order.order_id}`), {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
          });
          if (res.ok) {
            const body = await res.json();
            if (body?.invoiceData) {
              const fallback = convertOrderToInvoiceData(order);
              const clientTaxRate = SystemSettingsService.getInvoiceSettings().taxRate;
              const subtotal = Number(body.invoiceData.subtotal) || 0;
              const serverCompany = body.invoiceData.company;
              const company =
                serverCompany && !SystemSettingsService.isPlaceholderCompany(serverCompany)
                  ? serverCompany
                  : fallback.company;
              const taxRate =
                body.invoiceData.taxRate != null &&
                !SystemSettingsService.isPlaceholderCompany(serverCompany)
                  ? Number(body.invoiceData.taxRate)
                  : clientTaxRate;
              const taxAmount = subtotal * taxRate;
              const mapped: InvoiceData = {
                ...fallback,
                invoiceNumber: body.invoiceData.invoiceNumber || fallback.invoiceNumber,
                orderNumber: body.invoiceData.orderNumber || order.order_number,
                date: body.invoiceData.date || fallback.date,
                company,
                customer: {
                  name: body.invoiceData.customer?.name || fallback.customer.name,
                  company: body.invoiceData.customer?.company || '',
                  email: body.invoiceData.customer?.email || order.customer_email || '',
                  address: body.invoiceData.customer?.address || order.shipping_address || '',
                },
                items: (body.invoiceData.items || []).map((item: any) => ({
                  description: item.description,
                  quantity: Number(item.quantity) || 1,
                  unitPrice: Number(item.unitPrice) || 0,
                  total: Number(item.total) || 0,
                })),
                subtotal,
                taxRate,
                taxAmount,
                total: subtotal + taxAmount,
                notes: stripCheckoutMetadataFromNotes(
                  body.invoiceData.notes || order.notes || ''
                ),
                status: order.status,
              };
              if (!cancelled) {
                setInvoiceData(mapped);
                return;
              }
            }
          }

          const { ordersApi } = await import('../services/api');
          const response = await ordersApi.getById(order.order_id);
          const loaded = (response.data as any).order || response.data;
          const full = { ...order, ...loaded, items: loaded.items || [] };
          if (!cancelled) setInvoiceData(convertOrderToInvoiceData(full));
        } catch (err) {
          console.error('Failed to load order for invoice:', err);
          if (!cancelled) setInvoiceData(convertOrderToInvoiceData(order));
        } finally {
          if (!cancelled) setLoading(false);
        }
      })();
      return () => { cancelled = true; };
    }
  }, [open, order]);

  // Keep dialog preview identical to Download / Preview actions
  useEffect(() => {
    if (!invoiceData) {
      setPreviewHtml('');
      return;
    }
    const templateId = SystemSettingsService.getInvoiceSettings().template || 'modern';
    setPreviewHtml(SimpleInvoiceService.generateInvoiceHTML(invoiceData, templateId));
  }, [invoiceData]);

  const convertOrderToInvoiceData = (order: Order): InvoiceData => {
    const settings = SystemSettingsService.getSettings();
    const invoiceSettings = SystemSettingsService.getInvoiceSettings();
    const companyInfo = SystemSettingsService.getCompanyInfo();
    
    const invoiceNumber = `${settings.invoicePrefix}-${order.order_id.toString().padStart(6, '0')}`;
    const orderDate = order.created_at ? new Date(order.created_at).toLocaleDateString() : new Date().toLocaleDateString();
    const dueDate = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toLocaleDateString();

    const rawItems = Array.isArray(order.items) ? order.items : [];
    const items = rawItems.map((item: any) => {
      const qty = Number(item.quantity) || 1;
      const unit = Number(item.unit_price ?? item.price ?? 0) || 0;
      const line = Number(item.line_total) || unit * qty;
      return {
        description: orderItemInvoiceDescription(item),
        quantity: qty,
        unitPrice: unit,
        total: line,
      };
    });

    return {
      invoiceNumber,
      orderNumber: order.order_number,
      date: orderDate,
      dueDate,
      company: companyInfo,
      customer: {
        name: `${order.customer_first_name || ''} ${order.customer_last_name || ''}`.trim() || 'Customer',
        company: order.company_name || '',
        email: order.customer_email || '',
        address: order.shipping_address || '',
      },
      items,
      subtotal: parseFloat(String(order.total_amount)) || 0,
      taxRate: invoiceSettings.taxRate,
      taxAmount: (parseFloat(String(order.total_amount)) || 0) * invoiceSettings.taxRate,
      total: (parseFloat(String(order.total_amount)) || 0) * (1 + invoiceSettings.taxRate),
      notes: stripCheckoutMetadataFromNotes(order.notes) || '',
      status: order.status,
      paymentTerms: invoiceSettings.paymentTerms,
    };
  };

  const handlePreview = () => {
    if (invoiceData) {
      const templateId = SystemSettingsService.getInvoiceSettings().template;
      const html = SimpleInvoiceService.generateInvoiceHTML(invoiceData, templateId);
      const previewWindow = window.open('', '_blank');
      if (previewWindow) {
        previewWindow.document.write(html);
        previewWindow.document.close();
      }
    }
  };

  const handleDownload = () => {
    if (!invoiceData) {
      console.error('Download failed: invoice data not loaded yet');
      return;
    }
    setLoading(true);
    try {
      const templateId = SystemSettingsService.getInvoiceSettings().template || 'modern';
      SimpleInvoiceService.downloadInvoice(invoiceData, templateId);
      setSuccess('Invoice opened in a new tab (file download also attempted).');
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      console.error('Download failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    if (invoiceData) {
      setLoading(true);
      try {
        const templateId = SystemSettingsService.getInvoiceSettings().template;
        SimpleInvoiceService.printInvoice(invoiceData, templateId);
        setSuccess('Invoice sent to printer!');
        setTimeout(() => setSuccess(null), 3000);
      } catch (error) {
        console.error('Print failed:', error);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleEmail = () => {
    if (invoiceData && invoiceData.customer.email) {
      const subject = `Invoice ${invoiceData.invoiceNumber} - ${invoiceData.company.name}`;
      const body = `Dear ${invoiceData.customer.name},\n\nPlease find attached your invoice for order ${invoiceData.orderNumber}.\n\nThank you for your business!\n\nBest regards,\n${invoiceData.company.name}`;
      window.open(`mailto:${invoiceData.customer.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`);
    }
  };

  const handleCopyToClipboard = async () => {
    if (invoiceData) {
      try {
        const templateId = SystemSettingsService.getInvoiceSettings().template;
        await SimpleInvoiceService.copyInvoiceToClipboard(invoiceData, templateId);
        setSuccess('Invoice copied to clipboard!');
        setTimeout(() => setSuccess(null), 3000);
      } catch (error) {
        console.error('Copy failed:', error);
      }
    }
  };


  if (!order) return null;

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="lg" 
      fullWidth
      PaperProps={{
        sx: { minHeight: '80vh' }
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Invoice Generator</Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Invoice Information
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <strong>Invoice #:</strong> {invoiceData?.invoiceNumber}<br/>
              <strong>Order #:</strong> {invoiceData?.orderNumber}<br/>
              <strong>Customer:</strong> {invoiceData?.customer.name}<br/>
              <strong>Total:</strong> €{(invoiceData?.total ?? 0).toFixed(2)}<br/>
              <strong>Company:</strong> {invoiceData?.company.name}<br/>
              <strong>Template:</strong> {SystemSettingsService.getInvoiceSettings().template.charAt(0).toUpperCase() + SystemSettingsService.getInvoiceSettings().template.slice(1)}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Invoice template and company settings are configured by administrators in System Settings.
            </Typography>
          </Paper>

          {loading && !previewHtml ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : previewHtml ? (
            <Paper
              variant="outlined"
              sx={{
                height: '55vh',
                overflow: 'hidden',
                bgcolor: '#f5f5f5',
              }}
            >
              <Box
                component="iframe"
                title="Invoice preview"
                srcDoc={previewHtml}
                sx={{
                  width: '100%',
                  height: '100%',
                  border: 0,
                  bgcolor: 'white',
                }}
              />
            </Paper>
          ) : (
            <Alert severity="warning">Invoice preview could not be generated.</Alert>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button onClick={onClose} variant="outlined">
          Cancel
        </Button>
        <Button 
          onClick={handlePreview} 
          startIcon={<PreviewIcon />}
          variant="outlined"
        >
          Preview
        </Button>
        <Button 
          onClick={handleCopyToClipboard} 
          startIcon={<CopyIcon />}
          variant="outlined"
        >
          Copy HTML
        </Button>
        <Button 
          onClick={handleEmail} 
          startIcon={<EmailIcon />}
          variant="outlined"
          disabled={!invoiceData?.customer.email}
        >
          Email
        </Button>
        <Button 
          onClick={handlePrint} 
          startIcon={loading ? <CircularProgress size={16} /> : <PrintIcon />}
          variant="outlined"
          disabled={loading}
        >
          Print
        </Button>
        <Button 
          onClick={handleDownload} 
          startIcon={loading ? <CircularProgress size={16} /> : <DownloadIcon />}
          variant="contained"
          disabled={loading}
        >
          Download
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InvoiceGenerator;
